import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  useAccount,
  useBalance,
  useWalletClient,
  useSignTypedData,
} from "wagmi";
import { getContract, waitForTransaction } from "wagmi/actions";
import abi from "../../helpers/abi";
import { helperConstants as constants } from "../../helpers/helperConstants";
import { normalizeEther } from "../../helpers/digits";
import { closeModal } from "../../redux/counterSlice";
import { ModalContainer } from "./modalContainer";
import { ModalItemDetails } from "./modalComponents/modalItemDetails";
import useGetTokenPrice from "../../services/useGetTokenPrice";
import {
  CREATE_BORROW_EIP712_TYPES,
  CREATE_DYNAMIC_BORROW_EIP712_DOMAIN,
  LENDING_CONTRACT_ADDRESS,
} from "../../lib/constants";
import Link from "next/link";
import {
  approveNFTLending,
  approveSelectedCurrency,
} from "../../helpers/blockchainOperations";
import erc721ABI from "../../helpers/erc721ABI";
import { SelectNfts } from "./selectNfts";
import moment from "moment";
import { formatEther } from "viem";
import { errorToast } from "../toast";
import { handleConvert } from "../../helpers/handleConvert";
import { calcPercBigNumber } from "../../helpers/calculatePerc";
import { wrappedTokenABI } from "../../helpers/wrappedTokenABI";

const getOrderInput = ({
  offer,
  address,
  isGlobal,
  signingTime,
  // chain,
  itemId,
}) => ({
  issuer: address,
  nftAddresses: [offer.nftAddress],
  colleteralTokenIDs: [Number(itemId)],
  amounts: [Number(offer.amount) || 1],
  paymentToken: offer.paymentToken,
  borrowAmount: offer.borrowAmount,
  interestRate: Number(offer.interestRate),
  times: {
    signingTime,
    expiration: Number(offer.expiration),
    duration: Number(offer.duration),
  },
  orderKind: Number(offer.orderKind),
  tokenKinds: [Number(offer.tokenKind)],
  isGlobal,
});

interface Types {
  [key: string]: {
    title: string;
    buttonText: string;
    progressText: string;
    contractFn: string;
    fn?: any;
    toast: string;
    approve: boolean | Promise<any> | any;
    approveParams?: any[];
    tokenIdParam: boolean;
    checkBalance?: boolean;
    checkIssuerBalance?: boolean;
  };
}

const MODAL_KEY = "LendingModal";
const LendingModal = () => {
  const dispatch = useDispatch();
  const [nfts, setNfts] = useState<any[]>([]);
  const [signingTime, setSigningTime] = useState<number>(moment().unix());
  const { modal } = useSelector((state: any) => state.counter);
  const { address } = useAccount();
  const { item, collection, refresh, type, ...offer } = modal?.data;
  const isGlobal = offer?.isGlobal;
  const isListing = String(offer?.orderKind) === "1";

  const { signTypedDataAsync } = useSignTypedData({
    domain: CREATE_DYNAMIC_BORROW_EIP712_DOMAIN(item?.chain),
    types: CREATE_BORROW_EIP712_TYPES,
    message: getOrderInput({
      offer,
      address,
      isGlobal,
      signingTime,
      itemId: nfts[0],
    }),
    primaryType: "Order",
    account: address,
  });

  const { data: nativeBalance } = useBalance({ address, watch: true });
  const { data: wrappedTokenBalance } = useBalance({
    address,
    token: offer?.paymentToken,
    watch: true,
  });
  const { data: issuerWrappedTokenBalance } = useBalance({
    address: offer?.issuer,
    token: offer?.paymentToken,
    watch: true,
  });
  const totalBalance =
    parseFloat(nativeBalance?.formatted || "0") +
    parseFloat(wrappedTokenBalance?.formatted || "0");
  const isTotalBalanceEnough =
    totalBalance >= parseFloat(formatEther(offer?.borrowAmount || "0"));

  const { data: walletClient, status } = useWalletClient();

  const { usdPrice: tokenPrice, currentChainId } = useGetTokenPrice(
    item?.chain
  );

  const lendingContract = getContract({
    address: LENDING_CONTRACT_ADDRESS,
    abi,
    walletClient,
  });

  const selectedCurrencyContract = getContract({
    address: offer.paymentToken,
    abi: wrappedTokenABI,
    walletClient,
  });

  const offerContract = getContract({
    address: item?.collectionAddress,
    abi: erc721ABI,
    walletClient,
  });

  const types: Types = {
    cancel: {
      title: `Cancel the ${isGlobal ? "global" : ""} ${
        isListing ? "listing" : "offer"
      }`,
      buttonText: "Cancel",
      progressText: `Canceling the ${isGlobal ? "global" : ""} ${
        isListing ? "listing" : "offer"
      }...`,
      contractFn: "cancelOrder",
      // fn: isListing ? cancelLendOrderByID : cancelLendOfferByOfferID,
      toast: `${isGlobal ? "Global" : ""} ${
        isListing ? "Listing" : "Offer"
      } canceled!`,
      approve: false,
      tokenIdParam: false,
    },
    payback: {
      title: "Payback the loan",
      buttonText: "Payback",
      progressText: "Paybacking the loan...",
      contractFn: "payback",
      // fn: paybackLoanByOfferID,
      toast: "Loan paybacked!",
      approve: approveSelectedCurrency,
      approveParams: [selectedCurrencyContract, address, offer.borrowAmount],
      tokenIdParam: false,
      checkBalance: true,
    },
    liquidate: {
      title: "Liquidate the loan",
      buttonText: "Liquidate",
      progressText: "Liquidating the loan...",
      contractFn: "liquidate",
      // fn: liquidateLoanByOfferID,
      toast: "Loan liquidated!",
      approve: approveSelectedCurrency,
      approveParams: [selectedCurrencyContract, address, offer.borrowAmount],
      tokenIdParam: false,
    },
    accept: {
      title: `Accept the ${isGlobal ? "global" : ""} offer`,
      buttonText: "Accept",
      progressText: "Accepting the offer...",
      contractFn: isGlobal ? "acceptGlobalOffer" : "processOrder",
      // fn: isGlobal ? acceptGlobalLendOfferByOfferID : acceptLendOfferByOfferID,
      toast: "Offer accepted!",
      approve: approveNFTLending,
      approveParams: [offerContract, address],
      tokenIdParam: isGlobal ? true : false,
      checkIssuerBalance: true,
    },
  };

  const [buttonText, setButtonText] = useState(types[type].buttonText);

  const onSubmit = async () => {
    // console.log(id);

    if (status !== "success") return;
    if (types[type].checkBalance && !isTotalBalanceEnough)
      return errorToast("Not enough balance");
    if (types[type].checkIssuerBalance && !isIssuerBalanceEnough)
      return errorToast("Lender doesn't have enough balance");

    if (types[type].checkBalance)
      await handleConvert({
        amount: offer.borrowAmount,
        balance: wrappedTokenBalance,
        contract: selectedCurrencyContract,
        parse: false,
      });
    if (types[type].approve) {
      try {
        await types[type].approve(...types[type].approveParams);
      } catch (err) {
        return errorToast(err.reason ? err.reason : "err:" + err);
      }
    }

    setButtonText(types[type].progressText);
    try {
      const inputForOrderArg = [
        offer?.issuer,
        [item?.collectionAddress],
        // isGlobal ? [0] : [Number(offer?.colleteralTokenID)],
        [Number(offer?.colleteralTokenID)],
        [Number(offer?.amount)],
        offer?.paymentToken,
        offer?.borrowAmount,
        Number(offer?.interestRate),
        [
          Number(offer?.signingTime),
          Number(offer?.expiration),
          Number(offer?.duration),
        ],
        Number(offer?.orderKind),
        [Number(offer?.tokenKind)],
        isGlobal,
      ];

      // let signature: string, order: any;

      // if (type === "accept" && isGlobal) {
      //   setSigningTime(moment().unix());
      //   signature = await signTypedDataAsync();
      //   order = getOrderInput({
      //     offer,
      //     address,
      //     isGlobal,
      //     signingTime: signingTime,
      //     itemId: nfts[0],
      //   });
      // }

      const tx = await lendingContract.write[types[type].contractFn]([
        inputForOrderArg,
        offer?.signature,
        ...(types[type].tokenIdParam ? [nfts] : []),
      ]);

      await waitForTransaction({ hash: tx });

      // if (types[type].fn) {
      //   if (type === "accept" && isGlobal) {
      //     await types[type].fn({
      //       id: offer?.id,
      //       signature,
      //       order,
      //       chainId: item?.chain,
      //     });
      //   } else await types[type].fn({ id: offer?.id });
      // }
      toast.success((t) => (
        <span onClick={() => toast.dismiss(t.id)}>
          {/* Order cancelled! Here is your{" "} */}
          {types[type].toast} Here is your{" "}
          <Link
            href={`${
              constants?.helpers?.supportedChains?.find(
                (c: any) => c.hexId === currentChainId
              )?.scan_url
            }/tx/${tx}`}
          >
            <a target="_blank" rel="noopener noreferrer">
              <b>transaction.</b>
            </a>
          </Link>
        </span>
      ));

      refresh && refresh();
      dispatch(closeModal());
    } catch (err) {
      errorToast(err.reason ? err.reason : "err:" + err);
    } finally {
      // setButtonLoading(false);
      // refreshOffers();
      setButtonText(types[type].buttonText);
    }
  };

  const isIssuerBalanceEnough = !isGlobal
    ? parseFloat(issuerWrappedTokenBalance?.formatted || "0") >=
      parseFloat(formatEther(offer?.borrowAmount || "0"))
    : parseFloat(issuerWrappedTokenBalance?.formatted || "0") >=
      parseFloat(formatEther(offer?.borrowAmount || "0")) * nfts?.length;

  const amountWithFee = calcPercBigNumber(BigInt(offer?.borrowAmount), 99);

  const assetAmount = formatEther(offer.borrowAmount);
  const paybackAmount = Number(
    Number(assetAmount) +
      (Number(assetAmount) / 100) * Number(Number(offer?.interestRate) / 100)
  ).toFixed(4);

  const buttonDisabled =
    buttonText !== types[type].buttonText ||
    (types[type].checkBalance && !isTotalBalanceEnough) ||
    (types[type].checkIssuerBalance && !isIssuerBalanceEnough);

  return (
    <ModalContainer
      modalKey={MODAL_KEY}
      //   title={`Cancel ${isGlobal ? "Global" : ""} Offer`}
      title={types[type].title}
    >
      {/* <Toaster position='top-right' /> */}

      {/* <!-- Body --> */}
      <div className="modal-body p-6">
        {/* {!isGlobal && ( */}
        <ModalItemDetails
          item={item}
          collection={collection}
          isGlobal={isGlobal}
        />

        {/* )} */}
        {!isGlobal && (
          <div className="relative flex">
            <div className="mt-2 w-full">
              <div className="flex w-full items-center justify-between space-x-2">
                <div className="font-semibold">
                  {isListing ? "Current Listing Price:" : "Offer price:"}
                </div>
                <div className="flex w-fit space-x-1">
                  <span className="ml-auto flex items-center whitespace-nowrap">
                    {/* <ChainIcon
                      width={14}
                      name={item?.chain}
                      tooltip={getTokenName(item?.chain)}
                      sideGap={true}
                    /> */}
                    <span className="text-sm font-medium tracking-tight dark:text-jacarta-100">
                      {normalizeEther(offer?.borrowAmount)}{" "}
                      {offer?.paymentTokenName}
                    </span>
                  </span>
                  <div className="text-sm leading-7 dark:text-jacarta-300">
                    ~ $
                    {normalizeEther(
                      tokenPrice * normalizeEther(offer?.borrowAmount)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {type === "payback" && (
          <div className="relative flex">
            <div className="mt-2 w-full">
              <div className="flex w-full items-center justify-between space-x-2">
                <div className="font-semibold">Payback Amount:</div>
                <div className="ml-auto flex w-fit space-x-1 items-center">
                  <span className="text-sm font-medium tracking-tight dark:text-jacarta-100">
                    {parseFloat(paybackAmount).toFixed(4)}{" "}
                    {offer?.paymentTokenName}
                  </span>
                  <span className="text-sm leading-7 dark:text-jacarta-300">
                    ~ ${normalizeEther(tokenPrice * parseFloat(paybackAmount))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {isGlobal && type === "accept" && (
          <SelectNfts
            collectionAddress={offer?.nftAddress}
            walletAddress={address}
            amount={/* offer?.amount */ 1}
            setNfts={setNfts}
          />
        )}

        {type === "accept" && (
          <div className="my-3 alert-info w-fit p-2 rounded-lg">
            <p className="text-sm !normal-case">
              There is a 1% service fee on the Loan Amount.
              {(isGlobal ? !!nfts?.length : true) && (
                <span className="text-jacarta-700 dark:text-white !normal-case">
                  {" "}
                  You will receive{" "}
                  {!isGlobal
                    ? amountWithFee
                    : (
                        parseFloat(amountWithFee) * nfts?.length
                      ).toString()}{" "}
                  {offer?.paymentTokenName}
                </span>
              )}
            </p>
          </div>
        )}

        {type === "accept" &&
          !isIssuerBalanceEnough &&
          (!isGlobal ? true : !!nfts?.length) && (
            <div className="my-3 alert-error w-fit p-2 rounded-lg">
              <p className="text-sm !normal-case">
                Lender does not have enough {offer?.paymentTokenName} to
                complete the transaction.
              </p>
            </div>
          )}

        {/* <!-- Terms --> */}
      </div>
      {/* <!-- end body --> */}

      <div className="modal-footer">
        <div className="flex items-center justify-center space-x-4">
          {
            /*  modal?.data?.chain !== currentChainId ? (
              <button
                type="button"
                className="rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark"
                onClick={() => switchNetwork(modal?.data?.chain)}
              >
                Switch to{" "}
                {
                  supportedChains.find((c) => c.hexId === modal?.data?.chain)
                    ?.name
                }
              </button>
            ) : */
            (types[type].tokenIdParam ? nfts?.length : true) ? (
              <button
                type="button"
                className={`${
                  buttonDisabled ? "opacity-40" : ""
                } rounded-xl bg-accent py-3 px-8 text-center font-semibold text-white transition-all hover:bg-accent-dark`}
                onClick={onSubmit}
                //   disabled={buttonText !== "Cancel Order"}
                disabled={buttonDisabled}
              >
                {/* {buttonText} */}
                {types[type].checkBalance && !isTotalBalanceEnough
                  ? "Insufficient Balance"
                  : buttonText}
              </button>
            ) : (
              "Select NFTs"
            )
          }
        </div>
      </div>
    </ModalContainer>
  );
};

export default LendingModal;
